import { SearchBar, PipelineReports, MultipleToggableDropdown, STATUS_TYPES, Notification } from "dyl-components";
import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Form, Grid } from "semantic-ui-react";
import pipelineActions from "actions/pipeline";
import contactsActions from "actions/contacts";
import customerPipelineActions from "actions/customer_pipeline"
const MODULE_OPTIONS = [
  { key: "lead", value: "lead", text: "Lead" },
  { key: "opportunity", value: "opportunity", text: "Opportunity" },
  { key: "customer", value: "customer", text: "Customer" },
];

const PipelineReportsPage = () => {
  const [params, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const searchQuery = params.get("search") || "";
  const pipelineId = params.get("pipeline_id") || null;
  const module = params.get("module") || "lead";
  const user = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isSettings = pathname.split("/")[1] === "settings";


  const [selectedModule, setSelectedModule] = useState(module);
  const [selectedPipeline, setSelectedPipeline] = useState(pipelineId);
  const [selectedOwners, setSelectedOwners] = useState(isSettings ? [] : [
    { value: Number(user?.user_id), name: user?.name },
  ]);
  const [search, setSearch] = useState("");

  const { categories, isReadingCategories, pipeline_reports, isReadingPipelineReports, isUpdatingNewTransitioningPipeline } = useSelector(
    (state) => state.pipeline
  );
  const { assigned_owners = [], isReadingUserExtensions } = useSelector(
    (state) => state.contact
  );

  const pipelineCategories = useMemo(() => {
    if (!categories) return [];
    return categories?.filter((category) => category.account_stage === module)?.map((category) => ({
      key: String(category.id),
      value: String(category.id),
      text: category.name,
      stages:
        category.stages?.map((stage) => ({
          key: stage.id,
          value: stage.id,
          text: stage.name,
          sequence_id: stage.sequence_id
        })) || [],
    }));
  }, [categories, module]);

  const stages = useMemo(() => {
    if (!selectedPipeline) return null;

    const selectedCategory = pipelineCategories.find(
      (category) => category.value === selectedPipeline
    );

    return selectedCategory?.stages || [];
  }, [selectedPipeline, pipelineCategories]);

  const usersWithExtensions = useMemo(() => {
    return assigned_owners.map((owner) => ({
      text: `${owner.first_name} ${owner.last_name}`,
      key: owner.id,
      value: Number(owner.id),
      hasOptions: false,
      options: [],
      parent: null,
    }));
  }, [assigned_owners]);

  const nestedOwnerOptions = useMemo(() => {
    const includesLoggedInUser = usersWithExtensions.some(
      (u) => u.value === Number(user?.user_id)
    );

    const merged = [
      ...usersWithExtensions,
      ...(includesLoggedInUser
        ? []
        : [
          {
            text: user?.name,
            key: user?.user_id,
            value: Number(user?.user_id),
            hasOptions: false,
            options: [],
            parent: null,
          },
        ]),
    ];

    return merged.sort((a, b) => a.text.localeCompare(b.text));
  }, [usersWithExtensions, user]);

  useEffect(() => {
    if (!selectedModule) return;

    dispatch(
      pipelineActions.getPipelineCategories({
        module: selectedModule,
      })
    );

    !pipelineId && setSelectedPipeline(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedModule, dispatch]);

  useEffect(() => {
    dispatch(
      contactsActions.getOwnersAndCoOwners({
        list: "lead",
      })
    );
  }, [dispatch]);

  useEffect(() => {
    if (!selectedPipeline && pipelineCategories.length > 0) {
      setSelectedPipeline(pipelineCategories[0].value);
    }
  }, [pipelineCategories, selectedPipeline]);

  useEffect(() => {
    if (selectedPipeline) {
      dispatch(pipelineActions.getPipelineReports(selectedPipeline, {
        user_id: selectedOwners?.map((owner) => owner.value)?.join(","),
        search: searchQuery,
      }))
    }
  }, [selectedPipeline, dispatch, selectedModule, selectedOwners, searchQuery])

  const refresh = () => {
    dispatch(pipelineActions.getPipelineReports(selectedPipeline, {
      user_id: selectedOwners?.map((owner) => owner.value)?.join(","),
      search: searchQuery,
    }))
  }

  const onSearchSubmit = (value) => {
    const query = new URLSearchParams(params);
    query.set('search', value.trim());
    const query_string = query.toString();
    navigate(`${pathname}${query_string ? `?${query_string}` : ''}`);
  }
  const cancelFunction = () => {
    const query = new URLSearchParams(params);
    query.delete('search');
    const query_string = query.toString();
    navigate(`${pathname}${query_string ? `?${query_string}` : ''}`);
  }
  return (
    <Grid padded verticalAlign="top">
      <Grid.Row columns={2}>
        <Grid.Column width={6}>
          <div className="AccountOpportunity__searchBar">
            <SearchBar
              search={searchQuery}
              searchFcn={onSearchSubmit}
              cancelFcn={cancelFunction}
              placeholder="Search by Contact Name"
            />
          </div>
        </Grid.Column>

        <Grid.Column width={10}>
          <Form>
            <Form.Group style={{ justifyContent: "flex-end" }}>
              <Form.Field
                control={MultipleToggableDropdown}
                values={selectedOwners}
                className="CallsByAgent__MultipleToggableDropdown"
                nested_options={nestedOwnerOptions}
                onChange={(_, { value }) => {
                  setSearch("");
                  setSelectedOwners(value);
                }}
                placeholder="Owned By"
                onHoverParent={() => { }}
                searchValue={search}
                onSearchChange={(_, { value }) => setSearch(value)}
                loading={isReadingUserExtensions}
                isExpandable
                width={9}
              />

              <Form.Select
                placeholder="Select Module"
                options={MODULE_OPTIONS}
                value={selectedModule}
                onChange={(_, { value }) => {
                  setSelectedModule(value);
                  setSelectedPipeline(null);
                  setSearchParams({ module: value });
                }}
                width={3}
              />

              <Form.Select
                placeholder="Select Pipeline"
                options={pipelineCategories}
                value={selectedPipeline}
                onChange={(_, { value }) => {
                  setSelectedPipeline(value);
                  setSearchParams({ module: selectedModule, pipeline_id: value });
                }}
                loading={isReadingCategories}
                disabled={!selectedModule}
                width={4}
              />
            </Form.Group>
          </Form>
        </Grid.Column>
      </Grid.Row>

      <Grid.Row>
        <Grid.Column width={16}>
          {stages?.length > 0 && (
            <PipelineReports
              stages={stages?.map((stage) => ({
                id: stage.key,
                name: stage.text,
                items: pipeline_reports?.[stage?.text]?.[stage?.key]?.map((contact) => {
                  return {
                    id: contact?.person_id,
                    name: contact?.first_name + " " + contact?.last_name,
                    pipeline_id: contact?.pipeline_id,
                    account_id: contact?.account_id
                  }
                }) || [],
              }))}
              onDragEnd={async (data) => {
                try {
                  const toStage = stages.find((stage) => Number(stage.key) === Number(data.toStageId))
                  if (selectedModule === "customer") {
                    await dispatch(customerPipelineActions.update(data.item.account_id, {
                      pipeline_stage_id: Number(data.toStageId),
                      person_id: Number(data.item.id),
                      pipeline_id: data.item.pipeline_id,
                      close_reason_notes: "",
                      sequence_id: toStage?.sequence_id || null
                    }, { action: 'transition' }));
                  }
                  else {
                    await dispatch(pipelineActions.newTransition(data.item.pipeline_id, {
                      account_id: data.item.account_id,
                      pipeline_stage_id: Number(data.toStageId),
                      contact_id: Number(data.item.id),
                      sequence_id: toStage?.sequence_id
                    }, { sequence_done: true }))
                  }
                  Notification.alert(`Successfully updated stage!`, STATUS_TYPES.SUCCESS);
                  refresh()
                } catch (e) {
                  console.log(e);
                  Notification.alert(`Failed to update stage`, STATUS_TYPES.ERROR);
                }
              }}
              isLoading={isReadingPipelineReports || isUpdatingNewTransitioningPipeline}
            />
          )}
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
};

export default PipelineReportsPage;
